import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Code2, Rocket, Users } from 'lucide-react';
import { Card } from './ui/card';
import { personalInfo } from '../data/mock';

const About = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.2 });
  
  const highlights = [
    {
      icon: Code2,
      title: "Clean Code Advocate",
      description: "I write maintainable, scalable code following best practices and modern design patterns."
    },
    {
      icon: Rocket,
      title: "Performance Focused",
      description: "Optimizing applications for speed and efficiency, delivering exceptional user experiences."
    },
    {
      icon: Users,
      title: "Team Collaboration",
      description: "Experienced in leading teams, mentoring developers, and working in agile environments."
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: 'easeOut' }
    }
  };

  return (
    <section id="about" className="py-24 bg-slate-900 text-white relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-20 left-0 w-72 h-72 bg-emerald-500/5 rounded-full filter blur-3xl"></div>
      <div className="absolute bottom-20 right-0 w-96 h-96 bg-cyan-500/5 rounded-full filter blur-3xl"></div>
      
      <div className="max-w-6xl mx-auto px-6 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            About <span className="text-emerald-400">Me</span>
          </h2>
          <motion.div
            initial={{ width: 0 }}
            whileInView={{ width: 96 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="h-1 bg-gradient-to-r from-emerald-400 to-emerald-600 mx-auto rounded-full"
          />
        </motion.div>

        {/* Content Grid */}
        <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
          {/* Left - Avatar Card */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="aspect-square rounded-2xl bg-gradient-to-br from-emerald-500/10 to-slate-800 border-2 border-slate-700 overflow-hidden relative group">
              {/* Glassmorphism effect */}
              <div className="absolute inset-0 bg-slate-800/50 backdrop-blur-sm"></div>
              
              <div className="w-full h-full flex items-center justify-center relative z-10">
                <div className="text-center space-y-4 p-8">
                  <motion.div
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    className="w-32 h-32 mx-auto rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center text-6xl font-bold shadow-lg shadow-emerald-500/30"
                  >
                    {personalInfo.name.split(' ').map(n => n[0]).join('')}
                  </motion.div>
                  <div className="space-y-2">
                    <p className="text-xl font-semibold">{personalInfo.name}</p>
                    <p className="text-emerald-400 font-medium">{personalInfo.title}</p>
                    <p className="text-slate-400 text-sm">{personalInfo.location}</p>
                  </div>
                </div>
              </div>
              
              {/* Animated border */}
              <motion.div
                className="absolute inset-0 rounded-2xl"
                animate={{
                  boxShadow: [
                    '0 0 20px rgba(16, 185, 129, 0.2)',
                    '0 0 40px rgba(16, 185, 129, 0.4)',
                    '0 0 20px rgba(16, 185, 129, 0.2)'
                  ]
                }}
                transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
              />
            </div>
          </motion.div>

          {/* Right - About Content */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="space-y-6"
          >
            <h3 className="text-3xl font-bold mb-4 bg-gradient-to-r from-white to-emerald-400 bg-clip-text text-transparent">
              Background & Philosophy
            </h3>
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              viewport={{ once: true }}
              className="text-slate-300 leading-relaxed"
            >
              With over 4 years of experience in mobile and web development, I specialize in building high-performance, scalable applications using Flutter, React, and .NET technologies. My journey spans from mobile app development to leading development teams and delivering production-grade solutions.
            </motion.p>
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              viewport={{ once: true }}
              className="text-slate-300 leading-relaxed"
            >
              I believe in writing clean, maintainable code following best practices and modern design patterns. My approach combines technical expertise with strong team collaboration, having successfully mentored junior developers and led teams in delivering complex projects from concept to production.
            </motion.p>
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              viewport={{ once: true }}
              className="text-slate-300 leading-relaxed"
            >
              Proficient in agile methodologies and Git workflows, I continuously explore cutting-edge technologies and best practices. Whether it's implementing CI/CD pipelines, optimizing application performance, or architecting scalable solutions, I'm passionate about creating exceptional user experiences.
            </motion.p>
          </motion.div>
        </div>

        {/* Highlights Cards */}
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          className="grid md:grid-cols-3 gap-8"
        >
          {highlights.map((item, index) => {
            const Icon = item.icon;
            return (
              <motion.div key={index} variants={itemVariants}>
                <motion.div whileHover={{ scale: 1.05, y: -5 }}>
                  <Card
                    className="bg-slate-800/50 border-slate-700 hover:border-emerald-500/50 transition-all duration-300 p-6 group backdrop-blur-sm h-full"
                  >
                    <div className="flex flex-col items-center text-center space-y-4">
                      <motion.div
                        whileHover={{ rotate: 360 }}
                        transition={{ duration: 0.5 }}
                        className="p-4 rounded-full bg-emerald-500/10 group-hover:bg-emerald-500/20 transition-colors duration-300"
                      >
                        <Icon className="h-8 w-8 text-emerald-400" />
                      </motion.div>
                      <h4 className="text-xl font-semibold text-white">{item.title}</h4>
                      <p className="text-slate-400">{item.description}</p>
                    </div>
                  </Card>
                </motion.div>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

export default About;